import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assetsMediaContentRemotionMigrateCreate, MediaContent, MediaContentResponse } from '@goldcast/api/content';
import ContentDialog from '../Sessions/uiComponents/ContentDialog/ContentDialog';
import useDialog from '@/components/organisms/useDialog';
import { canUseNewTimelineForUnmigratedLiveEventContent } from '@/player-config/src/utils/clipUtils';
import {
  isRemotionMigrationPossibleForContent,
  isRemotionMigrationNotStarted,
  isAlignedStreamAvailable,
  isLiveEventWithManualMigration,
  isLiveEventRecording
} from '@/player-config/src/utils/mediaContentUtils';
import Button from '@/components/atoms/Button/Button';
import useAnalytics from '@/hooks/useAnalytics';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

const EventMigrationPopup = ({
  content,
  enabled = true
}: {
  content: MediaContent | MediaContentResponse;
  enabled?: boolean;
}) => {
  const { isOpen, openDialog, closeDialog } = useDialog();
  const [isLoading, setIsLoading] = useState(false);
  const sourceRef = useRef<string>('OnLanding');

  const { trackEventMigrationTriggered, trackEventMigrationPostponed, trackEventMigrationDismissed } = useAnalytics();

  useEffect(() => {
    const shouldOpen =
      enabled &&
      isLiveEventRecording(content.media_source_type, content.event_type, content.broadcast_type) &&
      isRemotionMigrationNotStarted(content) &&
      isRemotionMigrationPossibleForContent(content) &&
      !isAlignedStreamAvailable(content.individual_recording_status) &&
      isLiveEventWithManualMigration(content);

    if (shouldOpen) {
      openDialog();
    }
  }, [content, openDialog, enabled]);

  const contentAlreadyInNewTimeline = useMemo(() => {
    return content && canUseNewTimelineForUnmigratedLiveEventContent(content);
  }, [content]);

  useEffect(() => {
    const openMigrationPopup = ({ source }: { source: string }) => {
      if (enabled) {
        sourceRef.current = source;
        openDialog();
      }
    };
    EventBus.on(CustomEvents.OpenEventMigrationPopup, openMigrationPopup);
    return () => {
      EventBus.off(CustomEvents.OpenEventMigrationPopup, openMigrationPopup);
    };
  }, [openDialog, enabled]);

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        closeDialog();
        trackEventMigrationDismissed(content.organization, sourceRef.current);
      } else {
        openDialog();
      }
    },
    [closeDialog, openDialog, trackEventMigrationDismissed, content.organization]
  );

  const onMigrateNow = useCallback(() => {
    setIsLoading(true);
    assetsMediaContentRemotionMigrateCreate({ id: content.id }).then(() => {
      setIsLoading(false);
      window.location.reload();
      closeDialog();
    });
    trackEventMigrationTriggered(content.organization, sourceRef.current);
  }, [content, closeDialog, trackEventMigrationTriggered]);

  const onLater = useCallback(() => {
    closeDialog();
    trackEventMigrationPostponed(content.organization, sourceRef.current);
  }, [closeDialog, trackEventMigrationPostponed, content.organization]);

  const title = useMemo(() => {
    return contentAlreadyInNewTimeline ? 'Speaker Layouts Unavailable for This Video' : 'New Editor Available';
  }, [contentAlreadyInNewTimeline]);

  const description = useMemo(() => {
    return contentAlreadyInNewTimeline
      ? 'Speaker layouts aren’t available for this recording. You’ll need to migrate the video to enable them. During migration, the recording will be temporarily unavailable.'
      : "Add overlays, music, and more. Works with the Agentic Video Editor. Migration takes roughly the video's duration and temporarily makes the video unavailable.";
  }, [contentAlreadyInNewTimeline]);

  return (
    <ContentDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title}
      titleSize="large"
      size="medium"
      backdropClassName="!bg-black !bg-opacity-80"
      showDivider={false}
    >
      <div className="mb-4 px-4 text-sm font-light">{description}</div>
      <div className="h-px w-full bg-[#E3E9F0]" />
      <div className="flex justify-end p-4">
        <div className="flex gap-4">
          <Button
            className="!text-sm"
            buttonSize="base"
            variation="outline"
            trackingId="discard-event-migration"
            onClick={onLater}
            disabled={isLoading}
          >
            Later
          </Button>
          <Button
            className="!text-sm"
            buttonSize="base"
            variation="filled"
            trackingId="migrate-event-now"
            onClick={onMigrateNow}
            disabled={isLoading}
          >
            Migrate Now
          </Button>
        </div>
      </div>
    </ContentDialog>
  );
};

export default EventMigrationPopup;
